import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { change } from 'redux-form';
import { push } from 'connected-react-router';
import { CardList, Dropdown, IconList, Modal } from 'common/components';
import { YieldForm } from 'containers';
import * as formatters from 'common/formatters';
import { kebab as KebabIcon } from 'common/svg/icons';
import { getLatestDiary, capitalizeString, sumArray } from 'common/utils';
import { toCommaNumber } from 'common/formatters';
import { base64url_encode } from 'util/base64';
import { updateMaxPlantingArea } from 'actions/settings';
import * as constants from 'common/constants';
import { ROUTE_ACTIVITY } from 'common/constants';
// crop list specific container
export class CropList extends Component {
  state = {
    active: null,
    modal: false, // for TC modal
  };

  toggleModal = id => {
    this.setState({ modal: !this.state.modal, id });
  };

  renderContent(crop, idx) {
    let data = [
      {
        label: `${toCommaNumber(crop.plantedArea.size)} ${capitalizeString(
          crop.plantedArea.type
        )}`,
        icon: 'fields',
      },
    ];
    // for Diary Field, show only the last of array
    const expectedYield = getLatestDiary(crop.expectedYield);
    let yieldType = 'Bushels';
    if (expectedYield) {
      yieldType = capitalizeString(expectedYield.yield.type);

      data.push({
        label: (
          <Fragment>
            {toCommaNumber(expectedYield.yield.size)} {yieldType}/
            {capitalizeString(expectedYield.yield.per)}
            <a
              onClick={() => this.toggleModal(crop.id)}
              className={'iconlist__cta'}
            >
              Yield History
            </a>
          </Fragment>
        ),
        icon: 'storagefill',
      });
    }

    crop.seedDate &&
      data.push({
        label: `${formatters.date(crop.seedDate)} - ${
          crop.harvestDate ? formatters.date(crop.harvestDate) : ''
        }`,
        icon: 'calendar',
      });

    return <IconList key={idx} list={data} />;
  }

  getMaxPlantingArea = crop => {
    const { crops, field } = this.props;
    if (crop) {
      const otherCrops = crops.filter(c => c.id !== crop.id);
      return toCommaNumber(
        field.size -
          sumArray(
            otherCrops.map(c => c && c.plantedArea && c.plantedArea.size)
          )
      );
    }
    return toCommaNumber(
      field.size - sumArray(crops.map(c => c.plantedArea.size))
    );
  };

  renderKebab = cropItem => {
    const {
      goToActivityPage,
      setDeleteCrop,
      activities,
      changeValue,
      field,
      resetActivityForm,
      updateMaxPlantingArea,
    } = this.props;
    const {
      crop: { name, variety },
      plantedArea,
      expectedYield,
    } = cropItem;

    // find the seeding activity and get the id
    const seedingActivity = activities.find(
      a =>
        a.fieldActivityType === 'SEEDING' &&
        a.crop &&
        a.crop.name === name &&
        a.crop.variety === variety
    );

    return (
      <Dropdown theme={['right', 'medium', 'kebab']} trigger={<KebabIcon />}>
        <IconList
          list={[
            {
              label: 'Edit',
              icon: 'edit',
              action: () => {
                resetActivityForm();
                updateMaxPlantingArea(this.getMaxPlantingArea(cropItem));
                if (!seedingActivity) {
                  changeValue('fieldName', JSON.stringify(field));
                  changeValue(
                    'crop',
                    JSON.stringify({ ...cropItem.crop, variety: null })
                  );
                  changeValue('plantedArea', plantedArea.size);
                  changeValue('expectedYield', expectedYield[0].yield.size);
                  changeValue('variety', variety);
                }
                goToActivityPage(seedingActivity);
              },
            },
            {
              label: 'Delete',
              icon: 'garbage',
              action: () => setDeleteCrop(cropItem),
            },
          ]}
          theme={['medium']}
        />
      </Dropdown>
    );
  };

  getCardData = (crop, index) => ({
    title: `${crop.crop.name} ${crop.crop.variety ? crop.crop.variety : ''}`,
    content: this.renderContent(crop, index),
    top: this.renderKebab(crop),
    // add action instead
    // link: `${constants.ROUTE_FIELDS_DETAIL}/${item.season}/${item.name}`,
  });

  render() {
    const {
      crops,
      changeValue,
      field,
      resetActivityForm,
      updateMaxPlantingArea,
    } = this.props;
    if (!crops) {
      return null;
    }

    return (
      <Fragment>
        <CardList
          cards={crops.map(this.getCardData)}
          emptyStateconfig={{
            message: 'Add the crop you plan to grow on this field.',
            btnMessage: 'Add Crop',
            showAdd:
              field.size > 0 &&
              crops.reduce(
                (planted, crop) => planted + crop.plantedArea.size,
                0
              ) < field.size,
            routeName: constants.ROUTE_ACTIVITY_ADD_SEEDING,
            action: () => {
              resetActivityForm();
              const maxPlantedArea = this.getMaxPlantingArea();
              updateMaxPlantingArea(maxPlantedArea);
              // populate field and size in seeding form
              changeValue('fieldName', JSON.stringify(field));
              changeValue('plantedArea', maxPlantedArea);
            },
          }}
        />
        {this.state.modal && (
          <Modal
            enableTopRightClose
            theme={['narrow']}
            onToggle={this.toggleModal}
          >
            <YieldForm id={this.state.id} closeForm={this.toggleModal} />
          </Modal>
        )}
      </Fragment>
    );
  }
}

CropList.propTypes = {
  activities: PropTypes.array.isRequired,
  crops: PropTypes.array.isRequired,
  onDeleteCrop: PropTypes.func,
  goToActivityPage: PropTypes.func,
  setDeleteCrop: PropTypes.func,
  id: PropTypes.string,
  changeValue: PropTypes.func.isRequired,
  resetActivityForm: PropTypes.func.isRequired,
  field: PropTypes.shape({
    name: PropTypes.string,
    size: PropTypes.number,
  }),
  updateMaxPlantingArea: PropTypes.func.isRequired,
};

const formSettings = {
  form: 'activityform',
};

const mapDispatch = {
  goToActivityPage: activity =>
    push(
      activity
        ? `${ROUTE_ACTIVITY}/${base64url_encode(activity.id)}/SEEDING`
        : `${ROUTE_ACTIVITY}/add/SEEDING/crop`
    ),
  changeValue: (fieldname, value) =>
    change(formSettings.form, fieldname, value),
  resetActivityForm: () => ({
    type: '@@redux-form/DESTROY',
    meta: { form: ['activityform'] },
  }),
  updateMaxPlantingArea: payload => updateMaxPlantingArea(payload),
};

export default connect(
  null,
  mapDispatch
)(CropList);



// WEBPACK FOOTER //
// ./src/containers/CropList/index.js